import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./Topbar.scss";
import Button from "../Button/Button";
import user from "../../assets/images/user.jpg";
import erplogo from "../../assets/images/erplogo.png";
import { useDispatch } from "react-redux";
import { clearUser } from "../../state/auth/authSlice";
import { jwtDecode } from "jwt-decode";

const BE_URL = process.env.REACT_APP_API_BASE_URL;

const Topbar = () => {
  const [menuData, setMenuData] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const tokens = JSON.parse(localStorage.getItem("ERPTokens"));
  const userrole = jwtDecode(tokens.accessToken).role_id;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const menuRefs = useRef({});

  const studentMenu = [
    {
      id: 1,
      name: "Home",
      islink: true,
      path: "/student",
      dropdowns: [],
    },
    {
      id: 2,
      name: "LMS",
      islink: false,
      path: "",
      dropdowns: [
        {
          id: 1,
          name: "Timetable",
          path: "/student/timetable",
        },
        {
          id: 2,
          name: "Resources",
          path: "/student/resources",
        },
        {
          id: 3,
          name: "Leaves",
          path: "/student/leaves",
        },
        {
          id: 4,
          name: "Results",
          path: "/student/results",
        },
      ],
    },
  ];

  const fetchMenuData = async () => {
    try {
      const response = await axios.get(`${BE_URL}/api/menu-data`, {
        headers: { Authorization: `Bearer ${tokens.accessToken}` },
      });
      const extrasMenuItem = {
        id: 12,
        name: "Extras",
        dropdowns: [
          {
            id: 1,
            name: "Resources",
            path: "/admin/extras/resources",
          },
          {
            id: 2,
            name: "Apply Leave",
            path: "/admin/extras/applyleave",
          },
          {
            id: 3,
            name: "Salary Slips",
            path: "/admin/extras/salarySlips",
          },
        ],
      };

      const dash = {
        id: 1,
        name: "Home",
        islink: true,
        path: "/admin",
        dropdowns: [],
      };

      // Combine the fetched menu data with the Extras menu item
      const updatedMenuData = [dash, ...response.data, extrasMenuItem];
      setMenuData(updatedMenuData);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        // Handle token refresh logic
        refreshToken();
      } else {
        console.error("Error fetching menu data", error);
      }
    }
  };

  const refreshToken = async () => {
    try {
      // Implement token refresh logic
      console.log("Refreshing token...");
    } catch (error) {
      console.error("Error refreshing token", error);
      handleLogout();
    }
  };

  useEffect(() => {
    if (userrole === 10) {
      setMenuData(studentMenu);
    } else {
      fetchMenuData();
    }

    // Handle resize for responsive design
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      let clickedInsideDropdown = false;

      Object.keys(menuRefs.current).forEach((key) => {
        if (
          menuRefs.current[key] &&
          menuRefs.current[key].contains(event.target)
        ) {
          clickedInsideDropdown = true;
        }
      });

      if (!clickedInsideDropdown) {
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Check if current path matches menu item or its dropdowns
  const isActive = (menuItem) => {
    if (menuItem.islink && location.pathname === menuItem.path) {
      return true;
    }

    if (menuItem.dropdowns && menuItem.dropdowns.length > 0) {
      return menuItem.dropdowns.some(
        (dropdown) => location.pathname === dropdown.path
      );
    }

    return false;
  };

  const toggleMenu = (menuName) => {
    setActiveMenu(activeMenu === menuName ? null : menuName);
  };

  const toggleMobileMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isMenuOpen) {
      setActiveMenu(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("ERPTokens");
    dispatch(clearUser());
    navigate("/login");
  };

  const navigateProfile = () => {
    navigate("/profile");
  };

  // Store ref for each menu item
  const setMenuRef = (id, el) => {
    menuRefs.current[id] = el;
  };

  return (
    <div className="topbar">
      <div className="topbar-logo">
        <img
          src={erplogo}
          alt="Chaitanyas Academy Logo"
          onClick={() => navigate("/")}
        />
      </div>

      <div
        className={`topbar-mobile-toggle ${isMenuOpen ? "active" : ""}`}
        onClick={toggleMobileMenu}
      >
        <span></span>
        <span></span>
        <span></span>
      </div>

      <div className={`topbar-contents ${isMenuOpen ? "mobile-open" : ""}`}>
        {menuData.map((menuItem) => (
          <div
            key={menuItem.id}
            ref={(el) => setMenuRef(menuItem.id, el)}
            className={`topbar-item ${isActive(menuItem) ? "active" : ""} ${
              activeMenu === menuItem.name ? "menu-open" : ""
            }`}
          >
            <div
              className="topbar-item-main"
              onClick={
                menuItem.islink
                  ? () => navigate(menuItem.path)
                  : () => toggleMenu(menuItem.name)
              }
            >
              <span>{menuItem.name}</span>
              {!menuItem.islink && menuItem.dropdowns.length > 0 && (
                <span className="dropdown-indicator"></span>
              )}
            </div>

            {menuItem.dropdowns && menuItem.dropdowns.length > 0 && (
              <div
                className={`dropdown-menu ${
                  activeMenu === menuItem.name ? "show" : ""
                }`}
              >
                {menuItem.dropdowns.map((dd) => (
                  <Link
                    key={dd.id}
                    to={dd.path}
                    className={location.pathname === dd.path ? "active" : ""}
                  >
                    {dd.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="topbar-actions">
        <div className="topbar-user" onClick={navigateProfile}>
          <div className="user-info">
            <span className="user-name">Profile</span>
          </div>
          <div className="user-avatar">
            <img src={user} alt="user" />
          </div>
        </div>
        {/* <button className="logout-button" onClick={handleLogout}>
          Logout
        </button> */}
      </div>
    </div>
  );
};

export default Topbar;
